import type { ProfilePredicateClause } from '@capture/shared';

export interface PredicateEditorProps {
  clauses: ProfilePredicateClause[];
  onChange: (next: ProfilePredicateClause[]) => void;
}

const OPS: ProfilePredicateClause['op'][] = ['equals', 'not-equals', 'exists', 'not-exists'];

export function PredicateEditor({ clauses, onChange }: PredicateEditorProps) {
  const update = (i: number, patch: Partial<ProfilePredicateClause>) => {
    const next = clauses.map((c, idx) => (idx === i ? { ...c, ...patch } : c));
    onChange(next);
  };

  const remove = (i: number) => onChange(clauses.filter((_, idx) => idx !== i));

  const add = () =>
    onChange([...clauses, { path: '', format: 'xml', op: 'exists' }]);

  return (
    <div className="predicate-editor">
      {clauses.length === 0 && (
        <div className="subtle">
          No clauses yet — add at least one for this profile to match anything.
        </div>
      )}
      {clauses.map((c, i) => (
        <div key={i} className="clause-row">
          {i > 0 && <span className="and">AND</span>}
          <select
            value={c.format}
            onChange={(e) =>
              update(i, { format: e.target.value as 'xml' | 'json' })
            }
            aria-label="Format"
          >
            <option value="xml">XML</option>
            <option value="json">JSON</option>
          </select>
          <input
            value={c.path}
            onChange={(e) => update(i, { path: e.target.value })}
            placeholder="XPath or JSONPath"
            aria-label="Path"
          />
          <select
            value={c.op}
            onChange={(e) =>
              update(i, { op: e.target.value as ProfilePredicateClause['op'] })
            }
            aria-label="Operator"
          >
            {OPS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          {(c.op === 'equals' || c.op === 'not-equals') && (
            <input
              value={c.value ?? ''}
              onChange={(e) => update(i, { value: e.target.value })}
              placeholder="value"
              aria-label="Value"
            />
          )}
          <button
            type="button"
            className="clause-remove"
            onClick={() => remove(i)}
            aria-label={`Remove clause ${i + 1}`}
          >
            ×
          </button>
        </div>
      ))}
      <button type="button" className="add-clause-btn" onClick={add}>
        + Add clause
      </button>
    </div>
  );
}
